/**
 * @fileoverview The third-party AI disclosure, shown before an instructor's
 * first recording (contract C1, App Review guideline 5.1.2(i)).
 *
 * The text, the processors and the version come from the server
 * (`GET /auth/ai-disclosure`), so the web and the iOS app always show the same
 * notice and a wording change needs no client release. "Agree and continue"
 * records the agreement (`POST /auth/ai-disclosure/accept`); "Not now", Escape
 * and the close button all cancel, and the caller does not start recording.
 *
 * `useAiDisclosureGate` wraps the dialog in a promise, so a recording entry
 * point reads as one line: `if (!(await ensureAiDisclosure())) return;`.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/studio/primitives";
import { getErrorMessage } from "@/lib/api";
import {
  acceptAiDisclosure,
  fetchAiDisclosure,
  isAiDisclosureVersionMismatch,
  needsAiDisclosure,
  withAcceptedAiDisclosure,
} from "@/lib/aiDisclosure";

const FALLBACK_TITLE = "Before you record";

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {boolean} [props.force] Show the notice even when the server reports
 *   it accepted (the caller just got a 403 that says otherwise).
 * @param {(version: string) => void} props.onAccepted
 * @param {() => void} props.onDismiss
 */
export function AiDisclosureDialog({ open, force = false, onAccepted, onDismiss }) {
  const [disclosure, setDisclosure] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [changed, setChanged] = useState(false);
  const [acceptError, setAcceptError] = useState(null);
  // Props read inside the loader without making them effect dependencies:
  // a parent re-render must not refetch the notice under the reader.
  const latest = useRef({ force, onAccepted });
  latest.current = { force, onAccepted };
  // Each load takes a ticket; closing the dialog voids every ticket, so a
  // response that lands after "Not now" can neither repaint nor accept.
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchAiDisclosure();
      if (seq !== loadSeq.current) return;
      // Accepted elsewhere (the iOS app, another tab) since this page loaded.
      if (!latest.current.force && data?.version && data.accepted_version === data.version) {
        latest.current.onAccepted(data.version);
        return;
      }
      setDisclosure(data);
    } catch (error) {
      if (seq !== loadSeq.current) return;
      setLoadError(getErrorMessage(error, "We couldn't load the notice. Check your connection and try again."));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setDisclosure(null);
      setLoadError(null);
      setLoading(false);
      setChanged(false);
      setAcceptError(null);
      setAccepting(false);
      return undefined;
    }
    load();
    return () => {
      loadSeq.current += 1;
    };
  }, [open, load]);

  const handleAgree = async () => {
    if (!disclosure?.version || accepting) return;
    setAccepting(true);
    setAcceptError(null);
    try {
      const result = await acceptAiDisclosure(disclosure.version);
      setAccepting(false);
      onAccepted(result?.accepted_version || disclosure.version);
    } catch (error) {
      setAccepting(false);
      if (isAiDisclosureVersionMismatch(error)) {
        // The wording changed while it was open. Agreement is to a specific
        // version, so show the new one rather than accept it unread.
        setChanged(true);
        setDisclosure(null);
        await load();
        return;
      }
      setAcceptError(getErrorMessage(error, "We couldn't save your answer. Try again."));
    }
  };

  const handleOpenChange = (next) => {
    if (next || accepting) return; // never abandon an in-flight accept
    onDismiss();
  };

  const providers = Array.isArray(disclosure?.providers) ? disclosure.providers : [];
  const paragraphs = Array.isArray(disclosure?.paragraphs) ? disclosure.paragraphs : [];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="bg-[var(--studio-overlay)] border-[var(--studio-divider-strong)] text-[var(--text-primary)] mx-4 max-w-[calc(100%-2rem)] sm:max-w-lg max-h-[85vh] overflow-y-auto"
        data-testid="ai-disclosure-dialog"
      >
        <DialogHeader>
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--accent-yellow-dim)]">
            <Sparkles className="h-5 w-5 text-[var(--accent-yellow-text)]" strokeWidth={1.5} />
          </div>
          <DialogTitle className="font-serif text-xl font-light tracking-tight">
            {disclosure?.title || FALLBACK_TITLE}
          </DialogTitle>
          <DialogDescription className="text-[var(--text-secondary)]">
            Hans uses third-party AI services to turn a recording into lesson notes.
          </DialogDescription>
        </DialogHeader>

        {changed && (
          <p
            role="status"
            className="rounded-lg border border-[var(--state-warning-border)] bg-[var(--state-warning-dim)] p-3 text-sm text-[var(--state-warning)]"
            data-testid="ai-disclosure-changed"
          >
            This notice was just updated. Please read the new version.
          </p>
        )}

        {loading && !disclosure && (
          <div className="flex items-center gap-2 py-6 text-sm text-[var(--text-tertiary)]" data-testid="ai-disclosure-loading">
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
            Loading the notice…
          </div>
        )}

        {loadError && !disclosure && (
          <div className="flex flex-col items-start gap-3 py-2" data-testid="ai-disclosure-error">
            <p role="alert" className="text-sm text-[var(--state-warning)]">{loadError}</p>
            <Button variant="ghost" type="button" onClick={() => load()} data-testid="ai-disclosure-retry">
              Try again
            </Button>
          </div>
        )}

        {disclosure && (
          <div className="flex flex-col gap-4 text-sm leading-relaxed text-[var(--text-secondary)]" data-testid="ai-disclosure-body">
            {paragraphs.map((paragraph, i) => (
              <p key={i}>{paragraph}</p>
            ))}
            {providers.length > 0 && (
              <div className="rounded-xl border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-4">
                <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--text-tertiary)]">
                  Services that receive lesson data
                </p>
                <ul className="flex flex-col gap-2" data-testid="ai-disclosure-providers">
                  {providers.map((provider) => (
                    <li key={provider.name} className="flex flex-col">
                      <span className="font-medium text-[var(--text-primary)]">{provider.name}</span>
                      <span className="text-[var(--text-tertiary)]">{provider.purpose}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p className="text-xs text-[var(--text-tertiary)]">
              Read more in our{" "}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--accent-yellow-text)] underline underline-offset-2"
              >
                Privacy Policy
              </a>
              .
            </p>
          </div>
        )}

        {acceptError && (
          <p role="alert" className="text-sm text-[var(--state-warning)]" data-testid="ai-disclosure-accept-error">
            {acceptError}
          </p>
        )}

        <DialogFooter className="gap-2 sm:gap-3">
          <Button
            variant="ghost"
            type="button"
            onClick={() => handleOpenChange(false)}
            disabled={accepting}
            data-testid="ai-disclosure-not-now"
          >
            Not now
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={handleAgree}
            disabled={!disclosure?.version || accepting}
            data-testid="ai-disclosure-agree"
          >
            {accepting ? "Saving…" : "Agree and continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Promise-shaped access to the disclosure for a recording entry point.
 *
 * `ensureAiDisclosure()` resolves true at once when nothing is owed (accepted,
 * or an older backend with no `ai_disclosure` on /auth/me), and otherwise opens
 * the dialog and resolves true on agreement, false on "Not now".
 * `ensureAiDisclosure({force: true})` always asks: it is for a start-upload
 * 403, where the server has just said the cached user is out of date.
 *
 * @param {{user: object, setUser?: Function, refreshUser?: Function}} auth
 * @returns {{ensureAiDisclosure: Function, aiDisclosureDialog: React.ReactElement}}
 */
export function useAiDisclosureGate({ user, setUser, refreshUser }) {
  const [request, setRequest] = useState(null);
  const resolverRef = useRef(null);
  const userRef = useRef(user);
  userRef.current = user;
  // What this page saw accepted: the server's version, and the version the
  // cached user named at that moment (older if the text changed since
  // /auth/me loaded). The cached user only catches up after the next render,
  // or not at all if the refresh fails, and a recording that continues
  // straight after "Agree" must not be asked again.
  const acceptedRef = useRef(null);

  const settle = useCallback((ok) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setRequest(null);
    resolve?.(ok);
  }, []);

  const ensureAiDisclosure = useCallback(({ force = false } = {}) => {
    const current = userRef.current;
    if (!force) {
      if (!needsAiDisclosure(current)) return Promise.resolve(true);
      const seen = current.ai_disclosure.current_version;
      const done = acceptedRef.current;
      if (done && (seen === done.version || seen === done.seenCurrent)) return Promise.resolve(true);
    }
    resolverRef.current?.(false); // a second request supersedes the first
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setRequest({ force });
    });
  }, []);

  const handleAccepted = useCallback(
    (version) => {
      acceptedRef.current = { version, seenCurrent: userRef.current?.ai_disclosure?.current_version ?? null };
      setUser?.((prev) => withAcceptedAiDisclosure(prev, version));
      refreshUser?.();
      settle(true);
    },
    [setUser, refreshUser, settle]
  );

  const handleDismiss = useCallback(() => settle(false), [settle]);

  // Leaving the page cancels a pending request rather than leaving it hanging.
  useEffect(() => () => resolverRef.current?.(false), []);

  const aiDisclosureDialog = (
    <AiDisclosureDialog
      open={request !== null}
      force={!!request?.force}
      onAccepted={handleAccepted}
      onDismiss={handleDismiss}
    />
  );

  return { ensureAiDisclosure, aiDisclosureDialog };
}

export default AiDisclosureDialog;
